export const TAG_SPATIAL_INTENTS = Object.freeze({
  AUTO_ZOOM: 'auto-zoom',
  COPY_LINK: 'copy-link',
  EXPLICIT_360: 'explicit-360',
  EXPLICIT_3D: 'explicit-3d',
  ISOLATE_3D: 'isolate-3d',
  TASK_FOCUS: 'task-focus',
  INFO: 'info',
});

export const TAG_SPATIAL_VIEWERS = Object.freeze({
  PANORAMA_360: '360',
  MODEL_3D: '3d',
  NONE: 'none',
});

export const TAG_SPATIAL_PROVENANCE = Object.freeze({
  FACILITY_SCAN_POSITION: 'facility-scan-position',
  LINKED_SOURCE: 'linked-source',
  MODEL_ELEMENTS: 'model-elements',
  POINT_3D: 'point-3d',
  NONE: 'none',
});

export const TAG_SPATIAL_REASONS = Object.freeze({
  TARGET_360_CURRENT: 'target-360-current',
  TARGET_3D_CURRENT: 'target-3d-current',
  TARGET_360_OTHER_FACILITY: 'target-360-other-facility',
  TARGET_3D_OTHER_FACILITY: 'target-3d-other-facility',
  CONFLICTING_360_PROVENANCE: 'conflicting-360-provenance',
  INCOMPLETE_360_ASSOCIATION: 'incomplete-360-association',
  NO_SPATIAL_TARGET: 'no-spatial-target',
  INFO_REQUESTED: 'info-requested',
  UNSUPPORTED_INTENT: 'unsupported-intent',
});

export const TAG_SPATIAL_RESULT_KINDS = Object.freeze({
  VIEWER: 'viewer',
  NAVIGATE_FACILITY: 'navigate-facility',
  CHOOSE_FACILITY: 'choose-facility',
  PANEL: 'panel',
  UNAVAILABLE: 'unavailable',
});

const SCAFFOLD_TAG_IDENTITIES = Object.freeze(['scaffold', 'forgescaffold']);

const AUTOMATIC_INTENTS = Object.freeze([
  TAG_SPATIAL_INTENTS.AUTO_ZOOM,
  TAG_SPATIAL_INTENTS.COPY_LINK,
  TAG_SPATIAL_INTENTS.TASK_FOCUS,
]);

const SUPPORTED_TAG_INTENTS = Object.freeze([
  ...AUTOMATIC_INTENTS,
  TAG_SPATIAL_INTENTS.EXPLICIT_360,
  TAG_SPATIAL_INTENTS.EXPLICIT_3D,
  TAG_SPATIAL_INTENTS.ISOLATE_3D,
  TAG_SPATIAL_INTENTS.INFO,
]);

const hasValue = (candidateValue) => candidateValue !== null
  && candidateValue !== undefined
  && String(candidateValue).trim() !== '';

const isScalarId = (idValue) => (typeof idValue === 'string' && idValue.trim() !== '')
  || (typeof idValue === 'number' && Number.isFinite(idValue));

const canonicalId = (idValue) => {
  if (!isScalarId(idValue)) return null;
  return typeof idValue === 'string' ? idValue.trim() : idValue;
};

const normalizeId = (idValue) => {
  const scalarId = canonicalId(idValue);
  return scalarId === null ? null : String(scalarId).toLowerCase();
};

const sameId = (left, right) => normalizeId(left) !== null
  && normalizeId(left) === normalizeId(right);

const hasPoint = (pointValue) => pointValue !== null && pointValue !== undefined;

const hasFinitePoint = (pointValue) => pointValue !== null
  && typeof pointValue === 'object'
  && Number.isFinite(pointValue.x)
  && Number.isFinite(pointValue.y)
  && Number.isFinite(pointValue.z);

const isNonOriginFinitePoint = (pointValue) => hasFinitePoint(pointValue)
  && (pointValue.x !== 0 || pointValue.y !== 0 || pointValue.z !== 0);

function baseResult(currentFacilityId, overrides = {}) {
  return {
    kind: TAG_SPATIAL_RESULT_KINDS.PANEL,
    viewer: null,
    facilityId: canonicalId(currentFacilityId),
    provenance: TAG_SPATIAL_PROVENANCE.NONE,
    image360Id: null,
    degraded: false,
    reason: TAG_SPATIAL_REASONS.NO_SPATIAL_TARGET,
    alternatives: [],
    ...overrides,
  };
}

function unsupportedResult(currentFacilityId) {
  return baseResult(currentFacilityId, {
    kind: TAG_SPATIAL_RESULT_KINDS.UNAVAILABLE,
    reason: TAG_SPATIAL_REASONS.UNSUPPORTED_INTENT,
  });
}

function requestedViewer(intent) {
  if (intent === TAG_SPATIAL_INTENTS.EXPLICIT_360) {
    return TAG_SPATIAL_VIEWERS.PANORAMA_360;
  }
  if (intent === TAG_SPATIAL_INTENTS.EXPLICIT_3D
    || intent === TAG_SPATIAL_INTENTS.ISOLATE_3D) {
    return TAG_SPATIAL_VIEWERS.MODEL_3D;
  }
  return null;
}

function isValid360Target(target, currentFacilityId) {
  const validProvenance = target.provenance === TAG_SPATIAL_PROVENANCE.FACILITY_SCAN_POSITION
    || target.provenance === TAG_SPATIAL_PROVENANCE.LINKED_SOURCE;
  const currentImageIsPresent = !sameId(target.facilityId, currentFacilityId)
    || isScalarId(target.image360Id);
  const foreignImageIsSafe = sameId(target.facilityId, currentFacilityId)
    || target.image360Id === null
    || target.image360Id === undefined
    || isScalarId(target.image360Id);
  return validProvenance && target.hasPoint === true
    && currentImageIsPresent && foreignImageIsSafe;
}

function isValid3dTarget(target) {
  if (target.provenance === TAG_SPATIAL_PROVENANCE.MODEL_ELEMENTS) {
    return target.hasModelElements === true;
  }
  return target.provenance === TAG_SPATIAL_PROVENANCE.POINT_3D
    && target.hasPoint === true;
}

function isValidTarget(target, currentFacilityId) {
  if (!target || !isScalarId(target.facilityId)) return false;
  if (target.viewer === TAG_SPATIAL_VIEWERS.PANORAMA_360) {
    return isValid360Target(target, currentFacilityId);
  }
  return target.viewer === TAG_SPATIAL_VIEWERS.MODEL_3D && isValid3dTarget(target);
}

function rankTarget(target, intent) {
  if (requestedViewer(intent) === TAG_SPATIAL_VIEWERS.PANORAMA_360) {
    return target.provenance === TAG_SPATIAL_PROVENANCE.FACILITY_SCAN_POSITION ? 0 : 1;
  }

  if (requestedViewer(intent) === TAG_SPATIAL_VIEWERS.MODEL_3D) {
    return target.provenance === TAG_SPATIAL_PROVENANCE.MODEL_ELEMENTS ? 0 : 1;
  }

  // A per-facility placement is the current point's authority. In particular,
  // a 3D point must suppress an older linked creation/source association.
  const ranks = {
    [TAG_SPATIAL_PROVENANCE.FACILITY_SCAN_POSITION]: 1,
    [TAG_SPATIAL_PROVENANCE.LINKED_SOURCE]: 2,
    [TAG_SPATIAL_PROVENANCE.MODEL_ELEMENTS]: 3,
  };
  if (target.provenance === TAG_SPATIAL_PROVENANCE.POINT_3D) {
    return target.legacyFallback === true ? 4 : 0;
  }
  return ranks[target.provenance] ?? Number.MAX_SAFE_INTEGER;
}

function selectTarget(targets, intent) {
  const viewer = requestedViewer(intent);
  return targets
    .filter((target) => !viewer || target.viewer === viewer)
    .map((target, index) => ({ target, index }))
    .sort((left, right) => rankTarget(left.target, intent) - rankTarget(right.target, intent)
      || left.index - right.index)[0]?.target ?? null;
}

function alternativeFromTarget(target) {
  return {
    facilityId: canonicalId(target.facilityId),
    viewer: target.viewer,
    provenance: target.provenance,
  };
}

function reasonForTarget(target, isCurrent) {
  if (target.viewer === TAG_SPATIAL_VIEWERS.PANORAMA_360) {
    return isCurrent
      ? TAG_SPATIAL_REASONS.TARGET_360_CURRENT
      : TAG_SPATIAL_REASONS.TARGET_360_OTHER_FACILITY;
  }
  return isCurrent
    ? TAG_SPATIAL_REASONS.TARGET_3D_CURRENT
    : TAG_SPATIAL_REASONS.TARGET_3D_OTHER_FACILITY;
}

function targetResult(target, currentFacilityId, alternatives = [], reasonOverride = null) {
  const isCurrent = sameId(target.facilityId, currentFacilityId);
  return baseResult(currentFacilityId, {
    kind: isCurrent
      ? TAG_SPATIAL_RESULT_KINDS.VIEWER
      : TAG_SPATIAL_RESULT_KINDS.NAVIGATE_FACILITY,
    viewer: target.viewer,
    facilityId: canonicalId(target.facilityId),
    provenance: target.provenance,
    image360Id: isCurrent && target.viewer === TAG_SPATIAL_VIEWERS.PANORAMA_360
      ? canonicalId(target.image360Id)
      : null,
    reason: reasonOverride || reasonForTarget(target, isCurrent),
    alternatives,
  });
}

function isAccessible(facilityId, accessibleFacilityIds) {
  return !Array.isArray(accessibleFacilityIds)
    || accessibleFacilityIds.some((accessibleId) => sameId(accessibleId, facilityId));
}

function isExplicitPositionTarget(target) {
  return target?.provenance === TAG_SPATIAL_PROVENANCE.FACILITY_SCAN_POSITION
    || (target?.provenance === TAG_SPATIAL_PROVENANCE.POINT_3D
      && target?.legacyFallback !== true);
}

function apiModelElementsTarget(target, currentFacilityId) {
  if (target?.viewer !== TAG_SPATIAL_VIEWERS.PANORAMA_360
    || target?.hasModelElements !== true
    || !isValid360Target(target, currentFacilityId)) return null;
  return {
    ...target,
    viewer: TAG_SPATIAL_VIEWERS.MODEL_3D,
    provenance: TAG_SPATIAL_PROVENANCE.MODEL_ELEMENTS,
    image360Id: null,
  };
}

function hasEqualRankConflict(targets, intent) {
  if (targets.length < 2) return false;
  const bestRank = Math.min(...targets.map((target) => rankTarget(target, intent)));
  const bestTargets = targets.filter((target) => rankTarget(target, intent) === bestRank);
  const signatures = new Set(bestTargets.map((target) => [
    target.viewer,
    target.provenance,
    normalizeId(target.image360Id),
  ].join('|')));
  return signatures.size > 1;
}

function currentTargetSelection(validCurrentTargets, accessibleCurrentTargets, intent) {
  const explicitPositionExists = AUTOMATIC_INTENTS.includes(intent)
    && accessibleCurrentTargets.some(isExplicitPositionTarget);
  const eligibleTargets = explicitPositionExists
    ? validCurrentTargets.filter((target) => target.provenance !== TAG_SPATIAL_PROVENANCE.LINKED_SOURCE)
    : validCurrentTargets;
  const viewer = requestedViewer(intent);
  const viewerEligibleTargets = eligibleTargets.filter((target) => (
    !viewer || target.viewer === viewer
  ));
  const conflict = hasEqualRankConflict(viewerEligibleTargets, intent);
  return {
    target: conflict ? null : selectTarget(viewerEligibleTargets, intent),
    conflict,
  };
}

function selectedOtherTargets(validTargets, context, intent) {
  const facilityGroups = [];
  validTargets.filter((target) => !sameId(target.facilityId, context.currentFacilityId))
    .forEach((target) => {
      const facilityGroup = facilityGroups.find((group) => sameId(group.facilityId, target.facilityId));
      if (facilityGroup) facilityGroup.targets.push(target);
      else facilityGroups.push({ facilityId: target.facilityId, targets: [target] });
    });
  return facilityGroups.map((group) => selectTarget(group.targets, intent)).filter(Boolean);
}

function hasValidScaffoldSupplementScope(tag, currentFacilityId, intent) {
  const ownerIsAbsent = tag?.forgeAssetId === null || tag?.forgeAssetId === undefined;
  const ownerMatchesCurrent = isScalarId(tag?.forgeAssetId)
    && sameId(tag.forgeAssetId, currentFacilityId);
  if (isScalarId(tag?.facilityGroupId)) return ownerIsAbsent || ownerMatchesCurrent;
  const groupIsAbsent = tag?.facilityGroupId === null || tag?.facilityGroupId === undefined;
  return groupIsAbsent
    && intent === TAG_SPATIAL_INTENTS.ISOLATE_3D
    // The whole family, not scaffolds alone. A container or permit placed from a 360 scan owns the
    // same real 3D point a scaffold does, and asking `isScaffoldTag` here contradicted this
    // function's own guard (`isScaffoldFamily`) - so those two types silently lost Isolate, and the
    // 360 popup lost its 3D option with it. current3dPointSupplement cannot cover them either: it
    // skips the family on purpose, to leave the crop-around-its-own-box behaviour to this one.
    && isScaffoldFamily(tag)
    && ownerMatchesCurrent;
}

function currentScaffold3dSupplement(tag, suppliedTargets, context, intent) {
  if (requestedViewer(intent) !== TAG_SPATIAL_VIEWERS.MODEL_3D
    || !isScaffoldFamily(tag)
    || !isNonOriginFinitePoint(tag?.position)
    || !hasValidScaffoldSupplementScope(tag, context.currentFacilityId, intent)) return null;
  const validCurrentTargets = suppliedTargets.filter((target) => sameId(
    target?.facilityId,
    context.currentFacilityId,
  ) && isAccessible(target.facilityId, context.accessibleFacilityIds)
    && isValidTarget(target, context.currentFacilityId));
  if (validCurrentTargets.some((target) => target.viewer === TAG_SPATIAL_VIEWERS.MODEL_3D)
    || !validCurrentTargets.some((target) => target.hasPoint === true)) return null;
  return {
    facilityId: context.currentFacilityId,
    viewer: TAG_SPATIAL_VIEWERS.MODEL_3D,
    provenance: TAG_SPATIAL_PROVENANCE.MODEL_ELEMENTS,
    image360Id: null,
    hasPoint: true,
    hasModelElements: true,
  };
}

/**
 * A tag placed from a 360 scan still owns a real 3D point, so Isolate must be able to reach
 * the model even though the server advertised only the 360 placement. This supplements ONE
 * current-facility 3D point target for that case, and only for the explicit Isolate intent,
 * so automatic Zoom keeps preferring the exact placement scan.
 *
 * Deliberately narrow, so the existing rules survive:
 *   - any advertised 3D target (this facility OR another) suppresses it, otherwise a tag
 *     harvested elsewhere would stop redirecting there and silently zoom locally instead.
 *   - `hasModelElements` 360 targets are already promoted by apiModelElementsTarget, which
 *     produces a MODEL_ELEMENTS target that isolates rather than merely framing a point.
 *   - the scaffold family keeps currentScaffold3dSupplement (it crops around its own box).
 */
function current3dPointSupplement(tag, rawTargets, context, intent) {
  if (context?.allowPointOnly3d !== true) return null;
  if (intent !== TAG_SPATIAL_INTENTS.ISOLATE_3D || isScaffoldFamily(tag)) return null;
  if (rawTargets.some((target) => target?.viewer === TAG_SPATIAL_VIEWERS.MODEL_3D)) return null;
  const placementTarget = rawTargets.find((target) => target?.viewer === TAG_SPATIAL_VIEWERS.PANORAMA_360
    && target?.hasPoint === true
    && target?.hasModelElements !== true
    && sameId(target?.facilityId, context.currentFacilityId)
    && isValid360Target(target, context.currentFacilityId));
  if (!placementTarget) return null;
  return {
    facilityId: context.currentFacilityId,
    viewer: TAG_SPATIAL_VIEWERS.MODEL_3D,
    provenance: TAG_SPATIAL_PROVENANCE.POINT_3D,
    image360Id: null,
    hasPoint: true,
    hasModelElements: false,
  };
}

function authoritativeTargets(tag, context, intent) {
  const rawTargets = Array.isArray(tag?.spatialTargets) ? tag.spatialTargets : [];
  const scaffold3dTarget = currentScaffold3dSupplement(tag, rawTargets, context, intent);
  const point3dTarget = current3dPointSupplement(tag, rawTargets, context, intent);
  const useApiModelCapabilities = intent === TAG_SPATIAL_INTENTS.ISOLATE_3D
    && !isScaffoldTag(tag)
    && !(isScaffoldFamily(tag) && isScalarId(tag?.facilityGroupId));
  const apiModelTargets = useApiModelCapabilities
    ? rawTargets.map((target) => apiModelElementsTarget(
      target,
      context.currentFacilityId,
    )).filter(Boolean)
    : [];
  const suppliedTargets = [
    ...rawTargets,
    ...apiModelTargets,
    ...(scaffold3dTarget ? [scaffold3dTarget] : []),
    ...(point3dTarget ? [point3dTarget] : []),
  ];
  const accessibleTargets = suppliedTargets.filter((target) => isAccessible(
    target?.facilityId,
    context.accessibleFacilityIds,
  ));
  const validTargets = accessibleTargets.filter((target) => isValidTarget(
    target,
    context.currentFacilityId,
  ));
  const validCurrentTargets = validTargets.filter((target) => sameId(
    target.facilityId,
    context.currentFacilityId,
  ));
  const accessibleCurrentTargets = accessibleTargets.filter((target) => sameId(
    target?.facilityId,
    context.currentFacilityId,
  ));
  const currentSelection = currentTargetSelection(
    validCurrentTargets,
    accessibleCurrentTargets,
    intent,
  );
  return {
    currentTargets: validCurrentTargets,
    currentTarget: currentSelection.target,
    currentConflict: currentSelection.conflict,
    otherTargets: selectedOtherTargets(validTargets, context, intent),
  };
}

function isScaffoldFamily(tag) {
  const type = String(tag?.type || tag?.discriminator || '').toLowerCase();
  return type.includes('scaffold') || type.includes('permit') || type.includes('container');
}

function isScaffoldTag(tag) {
  const identities = [tag?.type, tag?.discriminator]
    .filter((identity) => identity !== null && identity !== undefined)
    .map((identity) => (typeof identity === 'string'
      ? identity.trim().toLowerCase()
      : null));
  return identities.length > 0
    && identities.every((identity) => SCAFFOLD_TAG_IDENTITIES.includes(identity));
}

function legacyHasExplicit3dPlacement(tag, currentFacilityId) {
  const provenance = tag?.positionProvenance
    || tag?.spatialPositionProvenance
    || tag?.currentPositionProvenance;
  const currentIsPositioned = Array.isArray(tag?.positionedFacilityIds)
    && tag.positionedFacilityIds.some((facilityId) => sameId(facilityId, currentFacilityId));
  return provenance === TAG_SPATIAL_PROVENANCE.POINT_3D
    || tag?.hasExplicit3dPosition === true
    || (!isScalarId(tag?.positionImage360Id) && currentIsPositioned);
}

function legacyTargetState(tag, context) {
  const ownerIsPresent = tag?.forgeAssetId !== null
    && tag?.forgeAssetId !== undefined
    && tag?.forgeAssetId !== '';
  const ownerMatchesCurrent = ownerIsPresent
    && sameId(tag.forgeAssetId, context.currentFacilityId);
  const currentIsPositioned = Array.isArray(tag?.positionedFacilityIds)
    && tag.positionedFacilityIds.some((facilityId) => sameId(
      facilityId,
      context.currentFacilityId,
    ));
  const positionScanIsCurrent = isScalarId(tag?.positionImage360Id)
    && Array.isArray(context.currentFacilityImageIds)
    && context.currentFacilityImageIds.some((imageId) => sameId(
      imageId,
      tag.positionImage360Id,
    ));
  const hasCurrentModelElements = Array.isArray(tag?.children) && tag.children.length > 0;
  const legacyPoint = isScaffoldFamily(tag) ? tag?.position : tag?.point;
  return {
    currentFacilityId: context.currentFacilityId,
    tagHasPoint: hasFinitePoint(legacyPoint),
    hasCurrentModelElements,
    hasCropCapability: isScaffoldFamily(tag)
      && (hasValue(tag?.cropVolumeJson) || hasPoint(tag?.cropBox)),
    explicit3dPlacement: legacyHasExplicit3dPlacement(tag, context.currentFacilityId),
    foreignOwner: ownerIsPresent && !ownerMatchesCurrent,
    currentPointIsOwned: ownerMatchesCurrent
      || (!ownerIsPresent && (currentIsPositioned || positionScanIsCurrent)),
  };
}

function legacyPositionTarget(tag, state) {
  if (isScalarId(tag?.positionImage360Id) && state.tagHasPoint && state.currentPointIsOwned) {
    return {
      facilityId: state.currentFacilityId,
      viewer: TAG_SPATIAL_VIEWERS.PANORAMA_360,
      provenance: TAG_SPATIAL_PROVENANCE.FACILITY_SCAN_POSITION,
      image360Id: tag.positionImage360Id,
      hasPoint: true,
      hasModelElements: state.hasCurrentModelElements,
    };
  }
  if (state.explicit3dPlacement && state.tagHasPoint && state.currentPointIsOwned) {
    return {
      facilityId: state.currentFacilityId,
      viewer: TAG_SPATIAL_VIEWERS.MODEL_3D,
      provenance: TAG_SPATIAL_PROVENANCE.POINT_3D,
      image360Id: null,
      hasPoint: true,
      hasModelElements: state.hasCurrentModelElements,
    };
  }
  return null;
}

function legacyLinkedTarget(tag, context, state) {
  const linkedImageId = tag?.linked360ImageInfo?.imageId;
  const linkedImageIsCurrent = Array.isArray(context.currentFacilityImageIds)
    && context.currentFacilityImageIds.some((imageId) => sameId(imageId, linkedImageId));
  if (state.explicit3dPlacement || !state.tagHasPoint
    || !isScalarId(linkedImageId) || !linkedImageIsCurrent) return null;
  return {
    facilityId: state.currentFacilityId,
    viewer: TAG_SPATIAL_VIEWERS.PANORAMA_360,
    provenance: TAG_SPATIAL_PROVENANCE.LINKED_SOURCE,
    image360Id: linkedImageId,
    hasPoint: true,
    hasModelElements: state.hasCurrentModelElements,
  };
}

function legacy3dFallbackTarget(tag, state) {
  if (state.hasCurrentModelElements || state.hasCropCapability) {
    return {
      facilityId: state.currentFacilityId,
      viewer: TAG_SPATIAL_VIEWERS.MODEL_3D,
      provenance: TAG_SPATIAL_PROVENANCE.MODEL_ELEMENTS,
      image360Id: null,
      hasPoint: state.tagHasPoint,
      hasModelElements: true,
    };
  }
  if (state.tagHasPoint && state.currentPointIsOwned
    && !state.explicit3dPlacement && !isScalarId(tag?.positionImage360Id)) {
    return {
      facilityId: state.currentFacilityId,
      viewer: TAG_SPATIAL_VIEWERS.MODEL_3D,
      provenance: TAG_SPATIAL_PROVENANCE.POINT_3D,
      image360Id: null,
      hasPoint: true,
      hasModelElements: false,
      legacyFallback: true,
    };
  }
  return null;
}

function buildLegacyTargets(tag, context) {
  const state = legacyTargetState(tag, context);
  if (state.foreignOwner) return [];
  return [
    legacyPositionTarget(tag, state),
    legacyLinkedTarget(tag, context, state),
    legacy3dFallbackTarget(tag, state),
  ].filter(Boolean);
}

function selectedFacilityTarget(currentTarget, otherTargets, context) {
  if (!isScalarId(context.selectedFacilityId)) return null;
  if (currentTarget && sameId(context.selectedFacilityId, context.currentFacilityId)) {
    return currentTarget;
  }
  return otherTargets.find((target) => sameId(
    target.facilityId,
    context.selectedFacilityId,
  )) || null;
}

function conflictingProvenanceReason(currentTarget, currentTargets) {
  if (currentTarget.provenance !== TAG_SPATIAL_PROVENANCE.FACILITY_SCAN_POSITION) return null;
  const linkedTarget = currentTargets.find((target) => target.viewer === TAG_SPATIAL_VIEWERS.PANORAMA_360
    && target.provenance === TAG_SPATIAL_PROVENANCE.LINKED_SOURCE);
  if (!linkedTarget || sameId(linkedTarget.image360Id, currentTarget.image360Id)) return null;
  return TAG_SPATIAL_REASONS.CONFLICTING_360_PROVENANCE;
}

function otherFacilityResult(otherTargets, currentFacilityId) {
  if (otherTargets.length === 1) {
    return targetResult(otherTargets[0], currentFacilityId);
  }
  if (otherTargets.length === 0) return null;
  return baseResult(currentFacilityId, {
    kind: TAG_SPATIAL_RESULT_KINDS.CHOOSE_FACILITY,
    reason: reasonForTarget(otherTargets[0], false),
    alternatives: otherTargets.map(alternativeFromTarget),
  });
}

function incomplete360Reason(tag, context, intent) {
  const requested = requestedViewer(intent);
  const incomplete360 = requested !== TAG_SPATIAL_VIEWERS.MODEL_3D
    && (Array.isArray(tag?.spatialTargets) ? tag.spatialTargets : [])
      .filter((target) => sameId(target?.facilityId, context.currentFacilityId))
      .some((target) => target?.viewer === TAG_SPATIAL_VIEWERS.PANORAMA_360
        && (isScalarId(target?.image360Id)
          || target?.provenance === TAG_SPATIAL_PROVENANCE.FACILITY_SCAN_POSITION)
        && target?.hasPoint !== true);
  return incomplete360
    ? TAG_SPATIAL_REASONS.INCOMPLETE_360_ASSOCIATION
    : TAG_SPATIAL_REASONS.NO_SPATIAL_TARGET;
}

function resolveFromTargets(tag, context, intent) {
  const {
    currentTargets, currentTarget, currentConflict, otherTargets,
  } = authoritativeTargets(tag, context, intent);
  const selectedTarget = selectedFacilityTarget(currentTarget, otherTargets, context);
  if (selectedTarget) return targetResult(selectedTarget, context.currentFacilityId);
  if (currentConflict) {
    return baseResult(context.currentFacilityId, {
      reason: TAG_SPATIAL_REASONS.CONFLICTING_360_PROVENANCE,
    });
  }

  if (currentTarget) {
    const reason = conflictingProvenanceReason(currentTarget, currentTargets);
    return targetResult(currentTarget, context.currentFacilityId,
      otherTargets.map(alternativeFromTarget), reason);
  }

  return otherFacilityResult(otherTargets, context.currentFacilityId)
    || baseResult(context.currentFacilityId, {
      reason: incomplete360Reason(tag, context, intent),
    });
}

/**
 * Resolves one hydrated tag into a deterministic, side-effect-free spatial plan.
 */
export function resolveTagSpatialIntent(tag, context = {}) {
  const intent = context.intent;
  if (!SUPPORTED_TAG_INTENTS.includes(intent)) {
    return unsupportedResult(context.currentFacilityId);
  }
  if (intent === TAG_SPATIAL_INTENTS.INFO) {
    return baseResult(context.currentFacilityId, {
      reason: TAG_SPATIAL_REASONS.INFO_REQUESTED,
    });
  }
  if (!isScalarId(context.currentFacilityId)) {
    return baseResult(null);
  }
  if (!isScalarId(tag?.id)) {
    return baseResult(context.currentFacilityId);
  }

  if (Object.prototype.hasOwnProperty.call(tag || {}, 'spatialTargets')) {
    return resolveFromTargets(tag, context, intent);
  }

  // Version-skew compatibility is deliberately current-facility only. It does
  // not inspect harvest or positioned-facility lists to invent a destination.
  return resolveFromTargets({
    ...tag,
    spatialTargets: buildLegacyTargets(tag, context),
  }, context, intent);
}

function taskBookkeeping(spatialPlan, includedTagIds, totalCount) {
  const unavailableCount = Math.max(0, totalCount - includedTagIds.length);
  return {
    ...spatialPlan,
    degraded: spatialPlan.degraded || unavailableCount > 0,
    partial: unavailableCount > 0,
    includedTagIds,
    unavailableCount,
  };
}

function actionableFacilityAlternatives(records, accessibleFacilityIds) {
  const alternatives = [];
  records.forEach(({ plan }) => {
    const candidates = plan?.kind === TAG_SPATIAL_RESULT_KINDS.CHOOSE_FACILITY
      ? plan.alternatives
      : (plan?.viewer && isScalarId(plan?.facilityId) ? [plan] : []);
    candidates.forEach((candidate) => {
      if (!isScalarId(candidate.facilityId)
        || !isAccessible(candidate.facilityId, accessibleFacilityIds)) return;
      if (candidate.viewer !== TAG_SPATIAL_VIEWERS.PANORAMA_360
        && candidate.viewer !== TAG_SPATIAL_VIEWERS.MODEL_3D) return;
      const alternative = {
        facilityId: canonicalId(candidate.facilityId),
        viewer: candidate.viewer,
        provenance: candidate.provenance,
      };
      if (!alternatives.some((existing) => sameId(existing.facilityId, alternative.facilityId)
        && existing.viewer === alternative.viewer
        && existing.provenance === alternative.provenance)) {
        alternatives.push(alternative);
      }
    });
  });
  return alternatives;
}

function hasValidPlanViewer(plan) {
  if (plan.viewer === TAG_SPATIAL_VIEWERS.PANORAMA_360) {
    return plan.provenance === TAG_SPATIAL_PROVENANCE.FACILITY_SCAN_POSITION
      || plan.provenance === TAG_SPATIAL_PROVENANCE.LINKED_SOURCE;
  }
  return plan.viewer === TAG_SPATIAL_VIEWERS.MODEL_3D
    && (plan.provenance === TAG_SPATIAL_PROVENANCE.MODEL_ELEMENTS
      || plan.provenance === TAG_SPATIAL_PROVENANCE.POINT_3D);
}

function hasSafeResolvedPlanIds(plan) {
  if (!plan || !isScalarId(plan.facilityId)) return false;
  const imageIdIsSafe = plan.image360Id === null
    || plan.image360Id === undefined
    || isScalarId(plan.image360Id);
  const alternativesAreSafe = plan.alternatives === undefined
    || (Array.isArray(plan.alternatives)
      && plan.alternatives.every((alternative) => isScalarId(alternative?.facilityId)
        && hasValidPlanViewer(alternative)));
  if (!imageIdIsSafe || !alternativesAreSafe) return false;
  if (plan.kind === TAG_SPATIAL_RESULT_KINDS.CHOOSE_FACILITY) {
    return Array.isArray(plan.alternatives);
  }
  if (plan.kind !== TAG_SPATIAL_RESULT_KINDS.VIEWER
    && plan.kind !== TAG_SPATIAL_RESULT_KINDS.NAVIGATE_FACILITY) return true;
  return hasValidPlanViewer(plan);
}

function resolvedTaskRecords(taskTags, context) {
  const suppliedPlans = new Map((Array.isArray(context.resolvedPlans) ? context.resolvedPlans : [])
    .filter((planEntry) => isScalarId(planEntry?.tagId)
      && hasSafeResolvedPlanIds(planEntry?.plan))
    .map((planEntry) => [normalizeId(planEntry.tagId), planEntry.plan]));
  return taskTags.map((tag) => ({
    tag,
    plan: suppliedPlans.get(normalizeId(tag.id)) || resolveTagSpatialIntent(tag, {
      ...context,
      intent: TAG_SPATIAL_INTENTS.AUTO_ZOOM,
    }),
  }));
}

function isActionableTaskRecord({ plan }, accessibleFacilityIds) {
  if (!hasSafeResolvedPlanIds(plan)
    || !isAccessible(plan.facilityId, accessibleFacilityIds)) return false;
  if (plan.kind === TAG_SPATIAL_RESULT_KINDS.VIEWER
    || plan.kind === TAG_SPATIAL_RESULT_KINDS.NAVIGATE_FACILITY) return true;
  return plan.kind === TAG_SPATIAL_RESULT_KINDS.CHOOSE_FACILITY
    && plan.alternatives?.some((alternative) => isAccessible(
      alternative.facilityId,
      accessibleFacilityIds,
    ));
}

function exact360TaskPlan(actionableRecords, context, totalCount) {
  const exact360Records = actionableRecords.filter(({ plan }) => plan.kind === TAG_SPATIAL_RESULT_KINDS.VIEWER
    && plan.viewer === TAG_SPATIAL_VIEWERS.PANORAMA_360
    && isScalarId(plan.image360Id));
  const firstExactPlan = exact360Records[0]?.plan;
  const shareFacilityScan = exact360Records.length > 0
    && exact360Records.length === actionableRecords.length
    && exact360Records.every(({ plan }) => sameId(plan.facilityId, firstExactPlan.facilityId)
      && sameId(plan.image360Id, firstExactPlan.image360Id));
  if (!shareFacilityScan) return null;
  const sharedTarget = {
    facilityId: firstExactPlan.facilityId,
    viewer: TAG_SPATIAL_VIEWERS.PANORAMA_360,
    provenance: firstExactPlan.provenance,
    image360Id: firstExactPlan.image360Id,
  };
  return taskBookkeeping(targetResult(sharedTarget, context.currentFacilityId),
    exact360Records.map(({ tag }) => canonicalId(tag.id)), totalCount);
}

function current3dTaskRecords(taskTags, context) {
  return taskTags.map((tag) => ({
    tag,
    plan: resolveTagSpatialIntent(tag, {
      ...context,
      selectedFacilityId: null,
      intent: TAG_SPATIAL_INTENTS.EXPLICIT_3D,
    }),
  })).filter(({ plan }) => plan.kind === TAG_SPATIAL_RESULT_KINDS.VIEWER
    && plan.viewer === TAG_SPATIAL_VIEWERS.MODEL_3D
    && sameId(plan.facilityId, context.currentFacilityId));
}

function aggregate3dTaskPlan(current3dRecords, context, totalCount) {
  if (current3dRecords.length === 0) return null;
  const hasModelElements = current3dRecords.some(({ plan }) => (
    plan.provenance === TAG_SPATIAL_PROVENANCE.MODEL_ELEMENTS
  ));
  const aggregateTarget = {
    facilityId: context.currentFacilityId,
    viewer: TAG_SPATIAL_VIEWERS.MODEL_3D,
    provenance: hasModelElements
      ? TAG_SPATIAL_PROVENANCE.MODEL_ELEMENTS
      : TAG_SPATIAL_PROVENANCE.POINT_3D,
    image360Id: null,
  };
  return taskBookkeeping(targetResult(aggregateTarget, context.currentFacilityId),
    current3dRecords.map(({ tag }) => canonicalId(tag.id)), totalCount);
}

function alternativeTaskPlan(actionableRecords, context, totalCount) {
  const alternatives = actionableFacilityAlternatives(
    actionableRecords,
    context.accessibleFacilityIds,
  );
  const includedTagIds = actionableRecords.map(({ tag }) => canonicalId(tag.id));
  if (alternatives.length === 1 && (actionableRecords.length === 1
    || alternatives[0].viewer === TAG_SPATIAL_VIEWERS.MODEL_3D)) {
    const singleAlternativePlan = targetResult(alternatives[0], context.currentFacilityId);
    return taskBookkeeping(singleAlternativePlan, includedTagIds, totalCount);
  }
  if (alternatives.length === 1) {
    return taskBookkeeping(baseResult(context.currentFacilityId), [], totalCount);
  }
  if (alternatives.length === 0) {
    return taskBookkeeping(baseResult(context.currentFacilityId), [], totalCount);
  }
  const facilityChoicePlan = baseResult(context.currentFacilityId, {
    kind: TAG_SPATIAL_RESULT_KINDS.CHOOSE_FACILITY,
    reason: reasonForTarget(alternatives[0], false),
    alternatives,
  });
  return taskBookkeeping(facilityChoicePlan, includedTagIds, totalCount);
}

/**
 * Aggregates task members without discarding members merely to force a 360.
 */
export function resolveTaskSpatialIntent(tags, context = {}) {
  if (context.intent !== TAG_SPATIAL_INTENTS.TASK_FOCUS) {
    return unsupportedResult(context.currentFacilityId);
  }
  if (!isScalarId(context.currentFacilityId)) {
    return taskBookkeeping(baseResult(null), [], Array.isArray(tags) ? tags.filter(Boolean).length : 0);
  }

  const suppliedTags = Array.isArray(tags) ? tags.filter(Boolean) : [];
  const taskTags = suppliedTags.filter((tag) => isScalarId(tag?.id));
  if (suppliedTags.length === 1) {
    if (taskTags.length === 0) return baseResult(context.currentFacilityId);
    return resolveTagSpatialIntent(taskTags[0], {
      ...context,
      intent: TAG_SPATIAL_INTENTS.AUTO_ZOOM,
    });
  }

  if (taskTags.length === 0) {
    return taskBookkeeping(baseResult(context.currentFacilityId), [], suppliedTags.length);
  }

  const actionableRecords = resolvedTaskRecords(taskTags, context)
    .filter((taskRecord) => isActionableTaskRecord(
      taskRecord,
      context.accessibleFacilityIds,
    ));
  return exact360TaskPlan(actionableRecords, context, suppliedTags.length)
    || aggregate3dTaskPlan(
      current3dTaskRecords(taskTags, context),
      context,
      suppliedTags.length,
    )
    || alternativeTaskPlan(actionableRecords, context, suppliedTags.length);
}
